import { ExtraWorkedTimeType } from "@src/appV2/Shifts/Shift/types";
import { z } from "zod";

import { type TimesheetStep } from "../../../ShiftDetails/Timekeeping/hooks/useTimesheetStep";

export function createSubmitTimesheetFormSchema(
  isCaliforniaFacility: boolean,
  currentStep?: TimesheetStep,
  isQrCodeSubmission = false,
  isUnverifiedTimesheetFlow = false
) {
  const isHcpSigningStep = currentStep === "hcp-signing";

  return z.object({
    facilityEmployeeName: z.string().min(1, "Please enter facility employee name"),
    // Make signature optional for QR code submissions
    facilityEmployeeSignature: isQrCodeSubmission
      ? z.string().optional()
      : z.string().min(1, "Please provide facility employee signature"),
    workerStation: isCaliforniaFacility
      ? z.string().min(1, "Please enter worker station")
      : z.string().optional(),
    workerNursingAssignment: isCaliforniaFacility
      ? z.string().min(1, "Please enter nursing assignment")
      : z.string().optional(),
    clockInTime: z.date(),
    clockOutTime: z.date(),
    breakStartTime: z.date().optional(),
    breakEndTime: z.date().optional(),
    breakAuthorizationReason: z.string().optional(),
    missedPunches: isUnverifiedTimesheetFlow
      ? z
          .array(
            z.object({
              type: z.literal(ExtraWorkedTimeType.FULL_SHIFT),
              reason: z
                .string()
                .min(1, "Please explain why you couldn't clock in/out normally")
                .min(10, "Please provide at least 10 characters"),
            })
          )
          .min(1, "At least one missed punch reason is required")
      : z
          .array(
            z.object({
              type: z.union([
                z.literal(ExtraWorkedTimeType.CLOCK_IN),
                z.literal(ExtraWorkedTimeType.CLOCK_OUT),
                z.literal(ExtraWorkedTimeType.BREAK),
              ]),
              reason: z.string(),
            })
          )
          .optional(),
    // California HCP fields - only required during HCP signing step
    hcpName:
      isCaliforniaFacility && isHcpSigningStep
        ? z.string().min(1, "Please enter your full name")
        : z.string().optional(),
    hcpSignature:
      isCaliforniaFacility && isHcpSigningStep
        ? z.string().min(1, "Please provide your signature")
        : z.string().optional(),
    // QR code specific fields
    timecard: isQrCodeSubmission
      ? z.object({
          url: z.string(),
          filename: z.string(),
          fileStorageFileKey: z.string(),
        })
      : z
          .object({
            url: z.string(),
            filename: z.string(),
            fileStorageFileKey: z.string(),
          })
          .optional(),
  });
}

export type SubmitTimesheetFormValues = z.infer<ReturnType<typeof createSubmitTimesheetFormSchema>>;
